import { useWidgetTokenStore } from '@elfsight/lib-utils';
import type { SourceId } from '../../../constants';
import type { PostLayoutType } from '../../../types';
import type {
  LinkMediaValue,
  MediaValue,
  PictureValueType
} from '../../data/sources';
import { usePreviewFetch } from '../../data/utils';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';
import { optimizeImagesSize } from './optimizeImagesSize';
import { usePostSettings } from './usePostSettings';

const VIDEO_PROXY_ENDPOINT = 'https://video-proxy.wu.elfsightcompute.com';

const fallbackCardLayoutRatio: Record<PostLayoutType, number> = {
  card1: 0.66,
  card2: 1,
  card3: 1,
  card4: 0.66,
  card5: 0.66
};

function transformMedia(
  media: MediaValue<string | PictureValueType[]> | undefined,
  provider: SourceId,
  widgetToken: string
) {
  if (Array.isArray(media?.src)) {
    return media;
  }

  const isProviderWithVideoProxy =
    provider === 'tiktok' || provider === 'twitter';

  if (isProviderWithVideoProxy && media?.type === 'video') {
    const proxyURL = new URL(VIDEO_PROXY_ENDPOINT);

    proxyURL.searchParams.append('url', media.src);
    proxyURL.searchParams.append('cache', '1');
    proxyURL.searchParams.append('widgetToken', widgetToken);

    return {
      ...media,
      src: proxyURL.toString()
    };
  }

  return media;
}

export const useMedia = (): MediaValue => {
  const { layout, postLayout } = useSocialFeedSettings();
  const { postImageRatio } = usePostSettings();
  const { rect, source, horizontal, url, media, repost } = usePost();
  const widgetTokenStore = useWidgetTokenStore();
  const ratio = postImageRatio || fallbackCardLayoutRatio[postLayout];

  const isFacebookLink = source.type === 'facebook' && media?.type === 'link';

  const previewLink = isFacebookLink && !media.src ? url : undefined;
  const previewLinkData = usePreviewFetch(previewLink);

  if (repost) {
    const { media: repostMedia } = repost;

    return optimizeImagesSize(
      repostMedia,
      source.type,
      rect,
      ratio,
      layout,
      horizontal
    );
  }

  const transformedMedia = transformMedia(
    media,
    source.type,
    widgetTokenStore.get()!
  );

  if (previewLinkData?.cover) {
    return {
      ...(transformedMedia as LinkMediaValue),
      src: previewLinkData.cover
    };
  }

  return optimizeImagesSize(
    transformedMedia,
    source.type,
    rect,
    ratio,
    layout,
    horizontal
  );
};
